import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { DEVICE_TYPES, ISeries, IYAxis } from 'app/data-studio-redesign/interfaces/data-studio.interface';
import { HIGHCHART_OPTIONS_DEFAULT, chartExportingOptions, getSeriesTableData } from 'app/data-studio-redesign/services/data-analysis-helper';
import * as Highcharts from 'highcharts';
import { delay, filter, skip, takeUntil } from 'rxjs/operators';
import { ReplaySubject, Subject } from 'rxjs';
import { DataAnalysisService } from 'app/data-studio-redesign/services/data-analysis-service';
import { CustomNumberFormatPipe, ThemeService } from '@wind-log_library/fe-component-library';
import { BsModalService } from 'ngx-bootstrap/modal';
import { SignalsChartYAxisPopupComponent } from './signals-chart-y-axis-popup/signals-chart-y-axis-popup.component';
import { cleanChart } from 'app/shared/helper/highcharts.helper';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import { DataExportType, ExcelExportService } from 'app/shared/services/export/excel/excel-export.service';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';
@Component({
    selector: 'app-signals-chart',
    templateUrl: './signals-chart.component.html',
    styleUrls: ['./signals-chart.component.scss'],
    standalone: false
})

export class SignalsChartComponent implements OnInit, OnChanges, OnDestroy {
  
  @Input() signalsData: { data: ISeries[], yAxis: IYAxis[] };
  @Input() removeItems: { ids: string[], yAxis: IYAxis[] };
  @Input() updateSeriesList: ISeries[];
  @Input() startTs: number;
  @Input() endTs: number;
  @Input() isReport: boolean;
  @Input() chartHeight: number;
  @Input() exportChart$: ReplaySubject<string>;
  @Output() updated = new EventEmitter();
  @Output() chartAxisUpdated = new EventEmitter();
  
  public highcharts = Highcharts;
  public update: boolean = false;
  public chartRef;
  public chartOptions: Highcharts.Options = { ...HIGHCHART_OPTIONS_DEFAULT };
  private signalsOrder: { [k: string]: string | number};
  public customAxisActive: boolean = false;
  private unsubscriber$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private dataAnalysisService: DataAnalysisService,
    private themeService: ThemeService,
    private modalService: BsModalService,
    private customNumberFormat: CustomNumberFormatPipe,
    private dateFormatPipe: DateTimeFormatPreferencePipe,
    private timezoneService: TimezoneService,
    private excelExportService: ExcelExportService
  ) {
    const self = this;
    this.chartOptions.tooltip = {
      shared: true,
      valueDecimals: 2,
      formatter: function () {
        const userOptions = this.points[0]?.series?.userOptions;
        const format = userOptions['tooltip']?.xDateFormat === '%b %y' ? 'my' : 'dmyt';

        return this.points?.reduce(function (s, point: any) {
          const formattedSignal = self.customNumberFormat.transform(Number(point.y).toFixed(2));
          return s + '<br/>' + `<span style="color:${point.color}"> ● </span>` + point?.series?.userOptions?.assetDisplayName + ' | ' + point.series.name + ': ' 
          +    '<b>' + formattedSignal + '</b>';
        }, '<div class="font-size-very-small">' + self.dateFormatPipe.transform(new Date(this.x), format) + '</div>');
      },
    }

    this.chartOptions.legend = {
      ...this.chartOptions.legend,
      events: {
        itemClick: () => false
      }
    }
  }

  public ngOnInit(): void {
    this.themeService.getTheme().pipe(takeUntil(this.unsubscriber$)).subscribe(() => {
      this.updateTooltipTheme();
    });

    this.timezoneService.getTimezoneObservable().pipe(takeUntil(this.unsubscriber$), skip(1)).subscribe(({timezone}) => {
      const gmtTimezone =  TimezoneService.ianaToGMT(timezone);
      /* This code updates the x axis title even if its not visible, but we need to update it for the xls export feature */
      this.chartRef?.xAxis[0]?.update({ title: {enabled: false, text: `Timestamp (${gmtTimezone})`}}, true);
    });

    if(this.exportChart$){
      this.exportChart$.pipe(takeUntil(this.unsubscriber$)).subscribe(d => this.export(d));
    }

    this.dataAnalysisService.timeSeriesSignalAdd$.pipe(takeUntil(this.unsubscriber$), filter(v => v), delay(100)).subscribe(() => {
      const series = [];
      this.signalsData.data.forEach(s => {
        const index = this.chartRef?.series?.findIndex(serie => serie.userOptions.id === s.id);
        index === -1 && s.chartData.length !== 0 && series.push(this.formatSingleSerie(s))
      })
      this.redrawChart(series);
    })

    this.dataAnalysisService.timeSeriesSignalChange$.pipe(takeUntil(this.unsubscriber$), filter(v => v)).subscribe(signal => {
      this.updateSerie(signal);
    });

    this.dataAnalysisService.timeSeriesPeriodChange$.pipe(takeUntil(this.unsubscriber$), filter(v => v)).subscribe(() => {
      this.deleteSeriesAndRedrawChart();
    });

    this.dataAnalysisService.timeSeriesSignalRemove$.pipe(takeUntil(this.unsubscriber$), filter(v => v)).subscribe(signals => {
      signals.ids.forEach(v => {
        const index = this.chartRef?.series?.findIndex(item => item.userOptions.id === v);
        const deletedSignalUnit = this.chartRef?.series?.find(item => item?.userOptions?.id === v)?.userOptions?.unit;
        index > -1 && this.chartRef?.series[index]?.remove();
        /* Check if there's not any signal with the same units than the one(s) we're deleting. If so, we remove the y-axis completely */
        if (this.chartRef?.series?.every(item => item.userOptions.unit !== deletedSignalUnit)) {
          index > -1 && this.chartRef?.yAxis[index]?.remove();
        }
      })
      this.chartOptions.yAxis = signals.yAxis;

      this.customAxisActive = this.signalsData.data.some(d => d.yAxisMin || d.yAxisMax);
      this.updateSeries();
    });

  }

  private updateTooltipTheme(): void {
    const tooltipTheme = {
      backgroundColor: this.themeService.getColor('--card'),
      style: {
        color: this.themeService.getColor('--primary')
      }
    };

    this.chartOptions.tooltip = {
      ...this.chartOptions.tooltip,
      ...tooltipTheme
    };
    this.chartRef?.update({ tooltip: tooltipTheme }, false);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.signalsData?.currentValue && !this.chartRef) {
      this.customAxisActive = this.signalsData.data.some(d => d.yAxisMin || d.yAxisMax);
      this.redrawChart();
    }

    if (this.isReport) {
      this.chartOptions.chart.height = this.chartHeight;
    } else {
      this.chartOptions.chart.height = 400;
    }
  }

  private updateYAxisValues(data: {custom: boolean, cancel: boolean, yaxis: {min: number, max: number, title: string}[]}): void {
    if (data.custom) {
      this.customAxisActive = true;
      data.yaxis.forEach((yAxis, i) => {
        const signalsSharingUnits = this.signalsData.data.filter(d => d.unit === yAxis.title);
        signalsSharingUnits.forEach(s => {s.yAxisMin = yAxis.min; s.yAxisMax = yAxis.max});
      });
    } else {
      this.customAxisActive = false;
      this.signalsData.data.forEach(s => {s.yAxisMin = null; s.yAxisMax = null})
    }
    this.deleteSeriesAndRedrawChart();
    this.chartAxisUpdated.emit();
  }

  public chartCallback: Highcharts.ChartCallbackFunction = chart => {
    if (!this.chartRef) {
      this.chartRef = chart;
    }
  };

  public openYAxisConfiguration(): void {
    const subject = new Subject<any>();
    const modal = this.modalService.show(SignalsChartYAxisPopupComponent, {
      backdrop: true,
      keyboard: true,
      ignoreBackdropClick: true,
      class: `modal-dialog-primary modal-md modal-dialog-centered ${this.themeService.getTheme().value}`
    });

    const yAxisData = this.signalsData.yAxis.map(y => {
      const index = this.chartRef.yAxis.findIndex(a => a.userOptions.title.text === y.title.text);
      return {
        min: this.chartRef.yAxis[index].min,
        max: this.chartRef.yAxis[index].max,
        title: this.chartRef.yAxis[index].userOptions.title.text,
        locked: this.chartRef.yAxis[index].userOptions.min !== null,
        order: index
      }
    }).sort((a, b) => a.order - b.order);

    modal.content.subject = subject;
    modal.content.yAxisData = yAxisData;
    modal.content.theme$ = this.themeService.getTheme();

    subject.toPromise().then((response: {custom: boolean, cancel: boolean, yaxis: {min: number, max: number, title: string}[]}) => {
      if (response.cancel) {
        return;
      }
      this.updateYAxisValues(response);
    });
  }

  private deleteSeriesAndRedrawChart(): void {
    cleanChart(this.chartRef);
    while (this.chartRef?.yAxis?.length) {
      this.chartRef?.yAxis[0]?.remove();
    }
    setTimeout(() => {
      this.redrawChart();
    }, 100);
  }

  private redrawChart(series = null): void {
    this.signalsOrder = Object.fromEntries(this.signalsData.data.map((k, i) => [k.id, i]));
    const chartSeries: Highcharts.SeriesOptionsType[] = (series ?? this.signalsData.data).map((item: any) => series ? item : this.formatSingleSerie(item));
    this.chartOptions.yAxis = this.signalsData.yAxis;
    setTimeout(() => {
      this.addSeries(chartSeries);
      this.updateSeries();
    }, 100)
  }

  public addSeries(series): void {
    if(this.chartRef.yAxis){
      series.forEach(item => {
        const index = this.signalsData.yAxis.findIndex(el => el.title?.text === item.unit);
        if (!this.chartRef.yAxis.find(el => el.userOptions?.title?.text === item.unit)) {
          this.chartRef.addAxis(this.signalsData.yAxis[index]);
        }
        const newIndex = this.chartRef.yAxis.findIndex(el => el.userOptions?.title?.text === item.unit)
        item.yAxis = newIndex;
        this.chartRef.addSeries(item);
      });
    }
  }

  public removeSeries() {
    this.removeItems.ids.forEach(id => {
      const index = this.chartRef?.series.findIndex(item => item.userOptions.id === id);
      index > -1 && this.chartRef?.series[index].remove();
    });
  }

  private updateSeries() {
    const timezone = this.timezoneService.selectedOption;
    const gmtTimezone= TimezoneService.ianaToGMT(timezone);

    if (this.chartRef?.xAxis && (this.startTs !== this.chartRef?.xAxis[0]?.min || this.endTs !== this.chartRef?.xAxis[0]?.max)) {
      this.chartRef?.xAxis[0]?.update({ title: {enabled: false, text: `Timestamp (${gmtTimezone})`}, min: this.startTs, max: this.endTs }, true);

    }
    this.updateSeriesList.forEach(item => {
      this.updateSerie(item)
    });

    this.updated.emit();
  }

  public updateSerie(item) {
    const index = this.chartRef.series?.findIndex(serie => serie.userOptions.id === item.id);
    const yAxis = this.chartRef.yAxis?.findIndex(el => el.userOptions?.title?.text === item.unit);
    const formattedSerie = this.formatSingleSerie(item);
    this.chartRef.series[index]?.update({ ...formattedSerie, yAxis, data: item.chartData });
  }

  public export(index): void {
    const chartOptions = chartExportingOptions(this.signalsData, this.themeService);
    switch (index) {
      case 0: {
        const { dataJSON } = getSeriesTableData(this.signalsData.data, 'time-series', this.dateFormatPipe,this.timezoneService.selectedOption);
        this.excelExportService.exportJSONasTable(dataJSON, 'Series chart', DataExportType.xlsx);
        break;
      };
      case 1: {
        const { dataJSON } = getSeriesTableData(this.signalsData.data, 'time-series', this.dateFormatPipe,this.timezoneService.selectedOption);
        this.excelExportService.exportJSONasTable(dataJSON, 'Series chart', DataExportType.csv);
        break;
      };
      case 2: this.chartRef?.exportChartLocal({type: 'image/png'}, chartOptions); break;
      case 3: this.chartRef?.exportChartLocal({type: 'application/pdf'}, chartOptions); break;
    }
  }

  private formatSingleSerie(serie) {
    const step = serie.chartTypeName === 'DATA_STUDIO.DATA_ANALYSIS.CHART_TYPE.LINE' ? null : serie.step;
    const isLineOrAreaChart = serie.type === 'line' || serie.type === 'area' || serie.type === 'areaspline';
    /* This is used to display the legend in the same format as on the line/area/areasplit types */
    const serieType = serie.type === 'scatter' ? 'line' : serie.type;
    return {
      id: serie.id,
      assetDisplayName: serie.turbine?.replace(/_/g, ' ') || serie.parkDisplayName || '',
      tooltip: {
        xDateFormat: serie.granularity === '30d' ? '%b %y' : '%d %b %y %H %M'
      },
      type: serieType,
      dashStyle: serie.dashStyle,
      lineWidth: serie.type === 'scatter' ? 0 : 2,
      marker: {
        enabled: !isLineOrAreaChart,
        symbol: !isLineOrAreaChart ? (serie.marker?.symbol || 'scatter') : 'scatter',
        radius: 3
      },
      states: {
        hover: {
          lineWidth: serie.type === 'scatter' ? 0.00001 : 2 /* 0 seems to be not working */
        }
      },
      legendIndex: Number(this.signalsOrder[serie.id]),
      step: step,
      name: (serie.deviceType !== DEVICE_TYPES.PARK ? serie.device.replace(/_/g, ' ') + ' | ' : '') + serie.signalName + ' [' + serie.unit + ']',
      yAxis: serie.yAxis,
      yAxisMin: serie.yAxisMin,
      yAxisMax: serie.yAxisMax,
      color: serie.color,
      data: serie.chartData,
      unit: serie.unit,
      visible: serie.visible
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber$.next();
    this.unsubscriber$.unsubscribe();
  }

}
